import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  Paper,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ArrowBack, Description, CheckCircle, Cancel, Schedule, Language, Info } from "@mui/icons-material";
import { formsService, DPForm, FormDetails } from "../services/forms-service";
import { personalDataService } from "../services/personal-data-service";

interface ConsentsPageProps {
  jwt: string | null;
  clientData: {
    subdomain: string;
    clientName: string;
  } | null;
}

const ConsentsPage: React.FC<ConsentsPageProps> = ({ jwt, clientData }) => {
  const location = useLocation();
  const theme = useTheme();
  const [displayJwt, setDisplayJwt] = useState(jwt);
  const [displayClientData, setDisplayClientData] = useState(clientData);
  const [context, setContext] = useState<any>(null);

  // Forms state
  const [forms, setForms] = useState<DPForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedForm, setSelectedForm] = useState<FormDetails | null>(null);
  const [loadingFormDetails, setLoadingFormDetails] = useState(false);
  const [vaultState, setVaultState] = useState<Record<string, { loading: boolean; value: string | null; error?: string }>>({});

  type DataElementDetailType = FormDetails["dataElements"][number];

  const getVaultKey = (element: DataElementDetailType, scope: "original" | "current") => {
    // Use consentHeaderId to make the key unique per form
    // For original: use the current form's consentHeaderId
    // For current: use the currentConsentHeaderId (the form where the current value came from)
    const consentHeaderId = scope === "original" ? selectedForm?.consentHeaderId : element.currentConsentHeaderId;
    const vaultId = scope === "original" ? element.vaultId : element.currentVaultId;

    // Use vaultId if available (unique per value), otherwise fall back to consentHeaderId + elementId
    if (vaultId && consentHeaderId) {
      return `${vaultId}-${scope}`;
    }
    // Fallback: use consentHeaderId + elementId to ensure uniqueness per form
    return `${consentHeaderId || "unknown"}-${element.dataElementId}-${scope}`;
  };

  // Handle navigation state from AuthenticatePage
  useEffect(() => {
    if (location.state?.jwt && location.state?.clientData) {
      setDisplayJwt(location.state.jwt);
      setDisplayClientData(location.state.clientData);
    }

    if (location.state?.context) {
      setContext(location.state.context);
    }
  }, [location.state]);

  // Load forms on component mount
  useEffect(() => {
    const loadForms = async () => {
      try {
        setLoading(true);
        setError(null);
        const formsData = await formsService.getForms();
        setForms(formsData);
      } catch (err: any) {
        setError(err.message || "Failed to load forms");
      } finally {
        setLoading(false);
      }
    };

    if (jwt) {
      loadForms();
    }
  }, [jwt]);

  const handleLogout = () => {
    // Clear the single portal JWT from localStorage
    localStorage.removeItem("portal_jwt");
    window.location.reload();
  };

  const handleFormClick = async (consentHeaderId: number) => {
    try {
      setLoadingFormDetails(true);
      // Clear only original values (form-specific), keep current values cached (shared across forms)
      setVaultState((prev) => {
        const newState: Record<string, { loading: boolean; value: string | null; error?: string }> = {};
        // Keep all current values (they can be shared across forms)
        Object.keys(prev).forEach((key) => {
          if (key.endsWith("-current")) {
            newState[key] = prev[key];
          }
        });
        return newState;
      });
      const formDetails = await formsService.getFormDetails(consentHeaderId);
      setSelectedForm(formDetails);
    } catch (err: any) {
      setError(err.message || "Failed to load form details");
    } finally {
      setLoadingFormDetails(false);
    }
  };

  const handleBackToList = () => {
    setSelectedForm(null);
  };

  const handleRevealValue = async (element: DataElementDetailType, scope: "original" | "current") => {
    const vaultId = scope === "original" ? element.vaultId : element.currentVaultId;
    const consentHeaderId = scope === "original" ? selectedForm?.consentHeaderId : element.currentConsentHeaderId;

    if (!vaultId || !consentHeaderId) {
      return;
    }

    const key = getVaultKey(element, scope);

    if (vaultState[key]?.loading) {
      return;
    }

    setVaultState((prev) => ({
      ...prev,
      [key]: {
        loading: true,
        value: prev[key]?.value ?? null,
        error: undefined,
      },
    }));

    try {
      const response = await personalDataService.getPersonalDataValues(consentHeaderId, [vaultId.toString()]);

      let value: string | null = null;
      if (response.success) {
        const dataItem = personalDataService.getVaultPersonalData(response, vaultId.toString());
        value = dataItem?.originalValue ?? null;
      }

      setVaultState((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          value,
          error: undefined,
        },
      }));
    } catch (err: any) {
      setVaultState((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          value: null,
          error: err?.message || "Failed to reveal value",
        },
      }));
    }
  };

  const getValuePresentation = (element: DataElementDetailType, scope: "original" | "current") => {
    const key = getVaultKey(element, scope);
    const storageMode = scope === "original" ? element.storageMode : element.currentStorageMode;
    const directValue = scope === "original" ? element.originalValue : element.currentValue;
    const vaultId = scope === "original" ? element.vaultId : element.currentVaultId;
    const consentHeaderId = scope === "original" ? selectedForm?.consentHeaderId : element.currentConsentHeaderId;
    const state = vaultState[key];

    const fallback = scope === "original" ? "Not provided" : "Not updated";

    if (storageMode !== "VAULT" || !vaultId) {
      return {
        text: directValue || fallback,
        highlight: Boolean(directValue),
        isProtected: false,
        canReveal: false,
        loading: false,
        error: undefined,
      };
    }

    if (state?.value) {
      return {
        text: state.value,
        highlight: true,
        isProtected: false,
        canReveal: false,
        loading: false,
        error: undefined,
      };
    }

    if (state?.loading) {
      return {
        text: "Decrypting…",
        highlight: false,
        isProtected: true,
        canReveal: false,
        loading: true,
        error: undefined,
      };
    }

    if (state?.error) {
      return {
        text: state.error,
        highlight: false,
        isProtected: true,
        canReveal: Boolean(consentHeaderId),
        loading: false,
        error: state.error,
      };
    }

    return {
      text: "Protected",
      highlight: false,
      isProtected: true,
      canReveal: Boolean(consentHeaderId),
      loading: false,
      error: undefined,
    };
  };

  // Show form details if selected or loading
  if (selectedForm || loadingFormDetails) {
    return (
      <Box>
        {/* Header with back button */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: "background.paper" }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton
              onClick={handleBackToList}
              color="primary"
              sx={{
                bgcolor: "primary.light",
                color: "white",
                "&:hover": { bgcolor: "primary.main" },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                {selectedForm?.collectionPointName || "Loading..."}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Form Details
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {loadingFormDetails ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 6, minHeight: "400px" }}>
            <CircularProgress size={50} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
              Loading form details...
            </Typography>
          </Box>
        ) : selectedForm ? (
          <Grid container spacing={3}>
            {/* Form Header */}
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Description color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Form Information
                    </Typography>
                  </Stack>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Form Type
                        </Typography>
                        <Chip
                          label={formsService.getFormTypeDisplayName(selectedForm.collectionPointType)}
                          sx={{
                            backgroundColor: selectedForm.collectionPointType === "API" ? theme.palette.formType.api : theme.palette.formType.notice,
                            color: "white",
                            fontWeight: 500,
                          }}
                          size="medium"
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Stack spacing={1}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Schedule fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Submitted On
                          </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {formsService.formatConsentTimestamp(selectedForm.consentTimestamp)}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Stack spacing={1}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Language fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Language
                          </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedForm.languageCode.toUpperCase()}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Purposes */}
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <CheckCircle color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Purposes ({selectedForm.purposes.length})
                    </Typography>
                  </Stack>
                  <List>
                    {selectedForm.purposes.map((purpose, index) => (
                      <React.Fragment key={purpose.purposeId}>
                        <ListItem sx={{ px: 0, py: 2 }}>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                {purpose.purposeName}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  {purpose.purposeDescription}
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  <Chip
                                    icon={purpose.originalConsent ? <CheckCircle /> : <Cancel />}
                                    label={`Original: ${purpose.originalConsent ? "Consented" : "Denied"}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: purpose.originalConsent
                                        ? theme.palette.consentStatus.active
                                        : theme.palette.consentStatus.denied,
                                      color: "white",
                                      fontWeight: 500,
                                    }}
                                    variant="outlined"
                                  />
                                  <Chip
                                    icon={purpose.currentConsent ? <CheckCircle /> : <Cancel />}
                                    label={`Current: ${formsService.getConsentStatusText(purpose.originalConsent, purpose.currentConsent)}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: purpose.currentConsent
                                        ? theme.palette.consentStatus.active
                                        : theme.palette.consentStatus.denied,
                                      color: "white",
                                      fontWeight: 500,
                                    }}
                                  />
                                </Stack>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < selectedForm.purposes.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Data Elements */}
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Info color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Data Elements ({selectedForm.dataElements.length})
                    </Typography>
                  </Stack>
                  <List>
                    {selectedForm.dataElements.map((element, index) => (
                      <React.Fragment key={element.dataElementId}>
                        <ListItem sx={{ px: 0, py: 2 }}>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                {element.elementName}
                              </Typography>
                            }
                            secondary={(() => {
                              const originalPresentation = getValuePresentation(element, "original");
                              const currentPresentation = getValuePresentation(element, "current");

                              return (
                                <Box>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {element.elementDescription}
                                  </Typography>
                                  <Stack spacing={1.5}>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 1.5,
                                        bgcolor: originalPresentation.highlight
                                          ? theme.palette.mode === "dark"
                                            ? "rgba(76, 175, 80, 0.15)"
                                            : "rgba(76, 175, 80, 0.08)"
                                          : theme.palette.mode === "dark"
                                          ? "rgba(255,255,255,0.05)"
                                          : "grey.50",
                                        border: "1px solid",
                                        borderColor: originalPresentation.highlight
                                          ? theme.palette.mode === "dark"
                                            ? "rgba(76, 175, 80, 0.4)"
                                            : "rgba(76, 175, 80, 0.5)"
                                          : theme.palette.mode === "dark"
                                          ? "rgba(255,255,255,0.1)"
                                          : "grey.200",
                                      }}
                                    >
                                      <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Original Value
                                          </Typography>
                                          <Tooltip title="The value that was entered when this specific form was submitted." arrow placement="top">
                                            <Info fontSize="inherit" sx={{ fontSize: "0.875rem", color: "text.secondary", cursor: "help" }} />
                                          </Tooltip>
                                        </Stack>
                                        {element.storageMode === "VAULT" && (
                                          <Chip label="Protected" size="small" variant="outlined" color="default" />
                                        )}
                                      </Stack>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: originalPresentation.highlight ? 600 : 500,
                                          color: originalPresentation.highlight ? "success.main" : "text.primary",
                                          mt: 0.5,
                                        }}
                                      >
                                        {originalPresentation.text}
                                      </Typography>
                                      {originalPresentation.canReveal && (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          sx={{ mt: 1 }}
                                          onClick={() => handleRevealValue(element, "original")}
                                          disabled={originalPresentation.loading}
                                          startIcon={originalPresentation.loading ? <CircularProgress size={14} color="inherit" /> : undefined}
                                        >
                                          {originalPresentation.loading ? "Decrypting…" : "Reveal value"}
                                        </Button>
                                      )}
                                      {originalPresentation.error && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.75, display: "block" }}>
                                          {originalPresentation.error}
                                        </Typography>
                                      )}
                                    </Paper>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 1.5,
                                        bgcolor: element.valueChanged
                                          ? theme.palette.mode === "dark"
                                            ? "rgba(33, 150, 243, 0.12)"
                                            : "rgba(33, 150, 243, 0.06)"
                                          : theme.palette.mode === "dark"
                                          ? "rgba(255,255,255,0.05)"
                                          : "grey.50",
                                        border: "1px solid",
                                        borderColor: element.valueChanged
                                          ? theme.palette.mode === "dark"
                                            ? "rgba(33, 150, 243, 0.3)"
                                            : "rgba(33, 150, 243, 0.4)"
                                          : theme.palette.mode === "dark"
                                          ? "rgba(255,255,255,0.1)"
                                          : "grey.200",
                                      }}
                                    >
                                      <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Current Value
                                          </Typography>
                                          <Tooltip
                                            title="The latest value of this data element across all your forms. Shows the most recent value if you've updated it in a later form submission."
                                            arrow
                                            placement="top"
                                          >
                                            <Info fontSize="inherit" sx={{ fontSize: "0.875rem", color: "text.secondary", cursor: "help" }} />
                                          </Tooltip>
                                        </Stack>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                          {element.valueChanged && (
                                            <Chip label="Updated" size="small" variant="outlined" color="info" sx={{ fontSize: "0.7rem" }} />
                                          )}
                                          {element.currentStorageMode === "VAULT" && (
                                            <Chip label="Protected" size="small" variant="outlined" color="default" />
                                          )}
                                        </Stack>
                                      </Stack>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: currentPresentation.highlight ? 600 : 500,
                                          color: currentPresentation.highlight ? "success.main" : "text.primary",
                                          mt: 0.5,
                                        }}
                                      >
                                        {currentPresentation.text}
                                      </Typography>
                                      {currentPresentation.canReveal && (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          sx={{ mt: 1 }}
                                          onClick={() => handleRevealValue(element, "current")}
                                          disabled={currentPresentation.loading}
                                          startIcon={currentPresentation.loading ? <CircularProgress size={14} color="inherit" /> : undefined}
                                        >
                                          {currentPresentation.loading ? "Decrypting…" : "Reveal latest value"}
                                        </Button>
                                      )}
                                      {currentPresentation.error && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.75, display: "block" }}>
                                          {currentPresentation.error}
                                        </Typography>
                                      )}
                                    </Paper>
                                  </Stack>
                                </Box>
                              );
                            })()}
                          />
                        </ListItem>
                        {index < selectedForm.dataElements.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : null}
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Forms
          </Typography>
          <Tooltip
            title="This page shows all the forms you've filled out on websites or in this portal. Each form contains the information you provided and the permissions you granted."
            arrow
            placement="top"
          >
            <IconButton size="small" sx={{ color: "text.secondary" }}>
              <Info fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          All the forms you've submitted and the information you've shared
        </Typography>

        {/* Analytics Cards */}
        {!loading && !error && forms.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Forms Statistics Container */}
            <Grid item xs={12} sm={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "grey.50",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  Forms
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={4}>
                    <Card elevation={0} sx={{ p: 1.25, textAlign: "center", bgcolor: "transparent", border: "1px solid", borderColor: "divider" }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main", lineHeight: 1.2, mb: 0.5 }}>
                        {forms.length}
                      </Typography>
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.25}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                          Total
                        </Typography>
                        <Tooltip title="Total number of forms you've submitted across all websites and the portal." arrow placement="top">
                          <Info fontSize="inherit" sx={{ fontSize: "0.7rem", color: "text.secondary", cursor: "help" }} />
                        </Tooltip>
                      </Stack>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card elevation={0} sx={{ p: 1.25, textAlign: "center", bgcolor: "transparent", border: "1px solid", borderColor: "divider" }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "success.main", lineHeight: 1.2, mb: 0.5 }}>
                        {forms.filter((f) => f.collectionPointType === "API").length}
                      </Typography>
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.25}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                          Website
                        </Typography>
                        <Tooltip title="Forms submitted on external websites or applications." arrow placement="top">
                          <Info fontSize="inherit" sx={{ fontSize: "0.7rem", color: "text.secondary", cursor: "help" }} />
                        </Tooltip>
                      </Stack>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card elevation={0} sx={{ p: 1.25, textAlign: "center", bgcolor: "transparent", border: "1px solid", borderColor: "divider" }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "info.main", lineHeight: 1.2, mb: 0.5 }}>
                        {forms.filter((f) => f.collectionPointType === "NOTICE").length}
                      </Typography>
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.25}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                          Portal
                        </Typography>
                        <Tooltip title="Forms submitted directly in this data portal." arrow placement="top">
                          <Info fontSize="inherit" sx={{ fontSize: "0.7rem", color: "text.secondary", cursor: "help" }} />
                        </Tooltip>
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Purposes Statistics Container */}
            <Grid item xs={12} sm={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "grey.50",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  Purposes
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Card elevation={0} sx={{ p: 1.25, textAlign: "center", bgcolor: "transparent", border: "1px solid", borderColor: "divider" }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "warning.main", lineHeight: 1.2, mb: 0.5 }}>
                        {new Set(forms.flatMap((form) => form.purposes)).size}
                      </Typography>
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.25}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                          Unique
                        </Typography>
                        <Tooltip
                          title="Number of unique purposes across all your forms. Each purpose is counted only once, regardless of how many forms it appears in."
                          arrow
                          placement="top"
                        >
                          <Info fontSize="inherit" sx={{ fontSize: "0.7rem", color: "text.secondary", cursor: "help" }} />
                        </Tooltip>
                      </Stack>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card elevation={0} sx={{ p: 1.25, textAlign: "center", bgcolor: "transparent", border: "1px solid", borderColor: "divider" }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "text.secondary", lineHeight: 1.2, mb: 0.5 }}>
                        {forms.reduce((total, form) => total + form.purposes.length, 0)}
                      </Typography>
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.25}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                          Total Instances
                        </Typography>
                        <Tooltip
                          title="Total number of purpose instances across all forms. If the same purpose appears in multiple forms, it's counted multiple times."
                          arrow
                          placement="top"
                        >
                          <Info fontSize="inherit" sx={{ fontSize: "0.7rem", color: "text.secondary", cursor: "help" }} />
                        </Tooltip>
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Context Information - Only show if from email link */}
      {context && (
        <Alert
          severity="info"
          sx={{ mb: 2, py: 0.5, minHeight: "auto" }}
          icon={
            <Tooltip title="This shows how you accessed this page. You came here from an email link after submitting a form." arrow placement="top">
              <Info fontSize="small" />
            </Tooltip>
          }
        >
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              <strong>Access:</strong> {context.type} • {context.identifier}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Form ID: {context.collectionPointId}
            </Typography>
          </Stack>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Loading your forms...
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Error Loading Forms
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}

      {/* Empty State */}
      {!loading && !error && forms.length === 0 && (
        <Card elevation={2}>
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <Description sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              No Forms Found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You haven't filled out any forms yet. Forms will appear here once you submit consent forms.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Forms Grid */}
      {!loading && !error && forms.length > 0 && (
        <Grid container spacing={3}>
          {forms.map((form) => (
            <Grid item xs={12} sm={6} lg={4} key={form.consentHeaderId}>
              <Card
                elevation={2}
                sx={{
                  cursor: "pointer",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: 4,
                    transform: "translateY(-2px)",
                  },
                }}
                onClick={() => handleFormClick(form.consentHeaderId)}
              >
                <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                  {/* Header */}
                  <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {form.collectionPointName}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Schedule fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {formsService.formatConsentTimestamp(form.consentTimestamp)}
                        </Typography>
                      </Stack>
                    </Box>
                    <Chip
                      label={formsService.getFormTypeDisplayName(form.collectionPointType)}
                      sx={{
                        backgroundColor: form.collectionPointType === "API" ? theme.palette.formType.api : theme.palette.formType.notice,
                        color: "white",
                        fontWeight: 500,
                      }}
                      size="small"
                    />
                  </Stack>

                  {/* Purposes */}
                  <Box sx={{ mb: 2, flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      Purposes ({form.purposes.length}):
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {form.purposes.slice(0, 2).map((purpose, index) => (
                        <Chip key={index} label={purpose} size="small" variant="outlined" sx={{ fontSize: "0.75rem" }} />
                      ))}
                      {form.purposes.length > 2 && (
                        <Chip label={`+${form.purposes.length - 2} more`} size="small" variant="outlined" sx={{ fontSize: "0.75rem" }} />
                      )}
                    </Stack>
                  </Box>

                  {/* Data Elements */}
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Info fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {form.dataElements.length} data element{form.dataElements.length !== 1 ? "s" : ""} collected
                      </Typography>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ConsentsPage;
